import { useState } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import { useAdminI18n } from "@/hooks/useAdminI18n";

interface SeoSetting {
  key: string;
  label: string;
  description: string;
  value: string;
  type: "text" | "textarea" | "url";
}

const defaultSettings: SeoSetting[] = [
  {
    key: "site_name",
    label: "Website-Name",
    description: "Wird in Titeln und Schema-Markup verwendet",
    value: "Helvetik Journal",
    type: "text",
  },
  {
    key: "site_tagline",
    label: "Tagline",
    description: "Kurze Beschreibung der Website",
    value: "Die Stimme der Schweiz – Nachrichten, Analysen, Meinungen",
    type: "text",
  },
  {
    key: "site_url",
    label: "Website-URL",
    description: "Kanonische Basis-URL (ohne abschliessenden Schrägstrich)",
    value: "https://helvetik-journal.ch",
    type: "url",
  },
  {
    key: "meta_title_template",
    label: "Meta-Titel-Vorlage",
    description: "Verwenden Sie %title% und %site% als Platzhalter",
    value: "%title% | Helvetik Journal",
    type: "text",
  },
  {
    key: "meta_description_default",
    label: "Standard Meta-Beschreibung",
    description: "Wird verwendet, wenn kein spezifischer Meta-Text gesetzt ist",
    value: "Helvetik Journal – Aktuelle Nachrichten aus der Schweiz. Politik, Wirtschaft, Technologie und mehr.",
    type: "textarea",
  },
  {
    key: "og_image_default",
    label: "Standard Open Graph Bild",
    description: "Wird für Social Media Sharing verwendet, wenn kein Artikelbild gesetzt ist",
    value: "https://helvetik-journal.ch/og-default.jpg",
    type: "url",
  },
  {
    key: "twitter_handle",
    label: "Twitter/X Handle",
    description: "Ihr Twitter-Handle (mit @)",
    value: "@HelvetikJournal",
    type: "text",
  },
  {
    key: "google_news_publisher",
    label: "Google News Publisher-Name",
    description: "Exakter Name wie bei Google News registriert",
    value: "Helvetik Journal",
    type: "text",
  },
  {
    key: "robots_txt",
    label: "robots.txt Inhalt",
    description: "Steuert, welche Seiten von Suchmaschinen gecrawlt werden",
    value: `User-agent: *\nAllow: /\nDisallow: /admin/\nDisallow: /api/\n\nSitemap: https://helvetik-journal.ch/sitemap.xml\nSitemap: https://helvetik-journal.ch/sitemap-news.xml`,
    type: "textarea",
  },
];

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "NewsMediaOrganization",
  name: "Helvetik Journal",
  url: "https://helvetik-journal.ch",
  logo: {
    "@type": "ImageObject",
    url: "https://helvetik-journal.ch/logo.png",
    width: 200,
    height: 60,
  },
  sameAs: [
    "https://twitter.com/HelvetikJournal",
    "https://www.facebook.com/HelvetikJournal",
    "https://www.linkedin.com/company/helvetik-journal",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "editorial",
    email: "redaktion@helvetik-journal.ch",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Bahnhofstrasse 1",
    addressLocality: "Zürich",
    postalCode: "8001",
    addressCountry: "CH",
  },
};

const sitemapPreview = `<?xml version="1.0" encoding="UTF-8"?>
<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <sitemap>
    <loc>https://helvetik-journal.ch/sitemap-pages.xml</loc>
    <lastmod>2026-04-28</lastmod>
  </sitemap>
  <sitemap>
    <loc>https://helvetik-journal.ch/sitemap-articles.xml</loc>
    <lastmod>2026-04-28</lastmod>
  </sitemap>
  <sitemap>
    <loc>https://helvetik-journal.ch/sitemap-categories.xml</loc>
    <lastmod>2026-04-28</lastmod>
  </sitemap>
  <sitemap>
    <loc>https://helvetik-journal.ch/sitemap-news.xml</loc>
    <lastmod>2026-04-28</lastmod>
  </sitemap>
</sitemapindex>`;

const newsSitemapPreview = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"
        xmlns:news="http://www.google.com/schemas/sitemap-news/0.9">
  <url>
    <loc>https://helvetik-journal.ch/artikel/bundesrat-klimaschutz-massnahmen-2026</loc>
    <news:news>
      <news:publication>
        <news:name>Helvetik Journal</news:name>
        <news:language>de</news:language>
      </news:publication>
      <news:publication_date>2026-04-27T08:30:00+01:00</news:publication_date>
      <news:title>Bundesrat beschließt neue Klimaschutz-Massnahmen für 2026</news:title>
      <news:keywords>Klima, Bundesrat, Umwelt, CO2</news:keywords>
    </news:news>
  </url>
  <!-- ... weitere Artikel ... -->
</urlset>`;

type ActiveTab = "global" | "sitemap" | "schema" | "robots" | "checklist";

export default function AdminSEO() {
  const { t } = useAdminI18n();
  const [settings, setSettings] = useState<SeoSetting[]>(defaultSettings);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>("global");
  const [sitemapType, setSitemapType] = useState<"index" | "news">("index");

  const updateSetting = (key: string, value: string) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value } : s)));
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const tabs: { id: ActiveTab; label: string; icon: string }[] = [
    { id: "global", label: t("seo.tab.global"), icon: "ri-global-line" },
    { id: "sitemap", label: t("seo.tab.sitemap"), icon: "ri-map-line" },
    { id: "schema", label: t("seo.tab.schema"), icon: "ri-code-s-slash-line" },
    { id: "robots", label: t("seo.tab.robots"), icon: "ri-robot-line" },
    { id: "checklist", label: t("seo.tab.checklist"), icon: "ri-checkbox-circle-line" },
  ];

  const checklistItems = [
    { label: "Meta-Titel-Vorlage konfiguriert", done: true, category: "Grundlagen" },
    { label: "Standard Meta-Beschreibung gesetzt", done: true, category: "Grundlagen" },
    { label: "Kanonische URL konfiguriert", done: true, category: "Grundlagen" },
    { label: "robots.txt konfiguriert", done: true, category: "Technisch" },
    { label: "Sitemap-Index erstellt", done: true, category: "Technisch" },
    { label: "Google News Sitemap aktiv", done: true, category: "Technisch" },
    { label: "Organization Schema konfiguriert", done: true, category: "Schema" },
    { label: "NewsArticle Schema auf Artikelseiten", done: true, category: "Schema" },
    { label: "BreadcrumbList Schema aktiv", done: true, category: "Schema" },
    { label: "Author Schema auf Artikelseiten", done: true, category: "Schema" },
    { label: "Open Graph Tags auf allen Seiten", done: true, category: "Social" },
    { label: "Twitter Card Tags konfiguriert", done: true, category: "Social" },
    { label: "Standard OG-Bild gesetzt", done: true, category: "Social" },
    { label: "Twitter Handle konfiguriert", done: true, category: "Social" },
    { label: "Semantisches HTML (H1/H2/H3)", done: true, category: "Inhalt" },
    { label: "Alt-Text für Bilder", done: false, category: "Inhalt" },
    { label: "Interne Verlinkung aktiv", done: true, category: "Inhalt" },
    { label: "Verwandte Artikel auf Artikelseiten", done: true, category: "Inhalt" },
    { label: "Breadcrumbs auf allen Seiten", done: true, category: "Inhalt" },
    { label: "Lazy Loading für Bilder", done: true, category: "Performance" },
    { label: "Saubere URLs (keine Parameter)", done: true, category: "Performance" },
    { label: "Fokus-Keyword-Feld im Editor", done: true, category: "Redaktion" },
    { label: "SEO-Vorschau im Artikel-Editor", done: true, category: "Redaktion" },
    { label: "SEO-Warnungen im Editor", done: true, category: "Redaktion" },
  ];

  const categories = [...new Set(checklistItems.map((i) => i.category))];
  const doneCount = checklistItems.filter((i) => i.done).length;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-hj-dark">{t("seo.header.title")}</h1>
            <p className="text-sm text-hj-gray mt-0.5">
              {t("seo.header.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-sm text-emerald-600 flex items-center gap-1">
                <i className="ri-check-line"></i>
                {t("seo.saved")}
              </span>
            )}
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors"
            >
              {t("seo.save")}
            </button>
          </div>
        </div>

        {/* SEO Score */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-5 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-hj-dark">{t("seo.score.title")}</h2>
              <p className="text-xs text-hj-gray mt-0.5">
                {doneCount} {t("seo.score.subtitle")}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-3xl font-bold text-emerald-600">
                  {Math.round((doneCount / checklistItems.length) * 100)}%
                </p>
                <p className="text-xs text-hj-gray">{t("seo.score.label")}</p>
              </div>
              <div className="w-16 h-16 relative">
                <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f3f4f6" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15.9" fill="none"
                    stroke="#10b981" strokeWidth="3"
                    strokeDasharray={`${Math.round((doneCount / checklistItems.length) * 100)} 100`}
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="mt-4 w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-emerald-500 h-2 rounded-full transition-all"
              style={{ width: `${Math.round((doneCount / checklistItems.length) * 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-hj-gray-light p-1 rounded-lg w-fit flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-white text-hj-dark"
                  : "text-hj-gray hover:text-hj-dark"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Global SEO Tab */}
        {activeTab === "global" && (
          <div className="space-y-4">
            {settings
              .filter((s) => s.key !== "robots_txt")
              .map((setting) => (
                <div key={setting.key} className="bg-white rounded-lg border border-hj-gray-border p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <label className="text-sm font-semibold text-hj-dark">{setting.label}</label>
                      <p className="text-xs text-hj-gray mt-0.5">{setting.description}</p>
                    </div>
                    <span className="text-[10px] text-hj-gray font-mono bg-hj-gray-light px-2 py-0.5 rounded shrink-0 ml-4">
                      {setting.key}
                    </span>
                  </div>
                  {setting.type === "textarea" ? (
                    <textarea
                      value={setting.value}
                      onChange={(e) => updateSetting(setting.key, e.target.value)}
                      rows={3}
                      maxLength={500}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none"
                    />
                  ) : (
                    <input
                      type={setting.type}
                      value={setting.value}
                      onChange={(e) => updateSetting(setting.key, e.target.value)}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    />
                  )}
                  {setting.key === "meta_title_template" && (
                    <div className="mt-2 p-3 bg-hj-gray-light rounded-md">
                      <p className="text-xs text-hj-gray">
                        Vorschau:{" "}
                        <span className="text-hj-dark font-medium">
                          {setting.value
                            .replace("%title%", "Bundesrat beschließt neue Klimaschutz-Massnahmen")
                            .replace("%site%", "Helvetik Journal")}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              ))}

            {/* Google Search Preview */}
            <div className="bg-white rounded-lg border border-hj-gray-border p-5">
              <h3 className="text-sm font-bold text-hj-dark mb-4">{t("seo.global.preview")}</h3>
              <div className="p-4 bg-white rounded-md border border-hj-gray-border max-w-xl">
                <p className="text-xs text-hj-gray">
                  https://helvetik-journal.ch/artikel/bundesrat-klimaschutz-massnahmen-2026
                </p>
                <p className="text-base text-[#1a0dab] hover:underline cursor-pointer mt-0.5">
                  Bundesrat beschließt neue Klimaschutz-Massnahmen für 2026 | Helvetik Journal
                </p>
                <p className="text-sm text-hj-gray mt-1 leading-relaxed">
                  Der Schweizer Bundesrat hat am Mittwoch ein umfassendes Klimaschutz-Paket verabschiedet, das die CO2-Emissionen bis 2030 um 50 Prozent reduzieren soll.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Sitemap Tab */}
        {activeTab === "sitemap" && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-hj-gray-border p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-hj-dark">{t("seo.sitemap.title")}</h2>
                <div className="flex gap-1 bg-hj-gray-light p-1 rounded-md">
                  <button
                    onClick={() => setSitemapType("index")}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${sitemapType === "index" ? "bg-white text-hj-dark" : "text-hj-gray"}`}
                  >
                    {t("seo.sitemap.index")}
                  </button>
                  <button
                    onClick={() => setSitemapType("news")}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${sitemapType === "news" ? "bg-white text-hj-dark" : "text-hj-gray"}`}
                  >
                    {t("seo.sitemap.news")}
                  </button>
                </div>
              </div>
              <div className="bg-hj-gray-light rounded-md p-4 overflow-x-auto">
                <code className="text-xs text-hj-gray font-mono whitespace-pre leading-relaxed">
                  {sitemapType === "index" ? sitemapPreview : newsSitemapPreview}
                </code>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: "Sitemap Index", url: "/sitemap.xml", status: "Aktiv", count: "4 Sitemaps", icon: "ri-map-line" },
                { label: "Artikel-Sitemap", url: "/sitemap-articles.xml", status: "Aktiv", count: "28 URLs", icon: "ri-file-text-line" },
                { label: "Kategorie-Sitemap", url: "/sitemap-categories.xml", status: "Aktiv", count: "13 URLs", icon: "ri-folder-line" },
                { label: "Google News Sitemap", url: "/sitemap-news.xml", status: "Aktiv", count: "48h Artikel", icon: "ri-newspaper-line" },
              ].map((item) => (
                <div key={item.url} className="bg-white rounded-lg border border-hj-gray-border p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-hj-gray-light rounded-lg shrink-0">
                      <i className={`${item.icon} text-hj-gray`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-hj-dark">{item.label}</p>
                        <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{t("seo.sitemap.active")}</span>
                      </div>
                      <p className="text-xs text-hj-gray font-mono mt-0.5">{item.url}</p>
                      <p className="text-xs text-hj-gray mt-1">{item.count}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-amber-600 mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-amber-800">Google News Sitemap</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Die Google News Sitemap enthält nur Artikel der letzten 48 Stunden. Sie wird automatisch aktualisiert, wenn neue Artikel veröffentlicht werden. Stellen Sie sicher, dass Ihr Publisher-Name exakt mit dem bei Google News registrierten Namen übereinstimmt.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schema Tab */}
        {activeTab === "schema" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { type: "Organization", desc: "Globales Organisations-Schema für alle Seiten", icon: "ri-building-line", active: true },
                { type: "NewsArticle", desc: "Automatisch auf allen Artikelseiten", icon: "ri-newspaper-line", active: true },
                { type: "BreadcrumbList", desc: "Breadcrumb-Navigation auf allen Seiten", icon: "ri-arrow-right-s-line", active: true },
                { type: "Author (Person)", desc: "Autoren-Schema auf Artikelseiten", icon: "ri-user-line", active: true },
                { type: "WebSite", desc: "Sitelinks-Suchbox für Google", icon: "ri-global-line", active: true },
                { type: "FAQPage", desc: "Für FAQ-Seiten (optional)", icon: "ri-question-line", active: false },
              ].map((schema) => (
                <div key={schema.type} className="bg-white rounded-lg border border-hj-gray-border p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${schema.active ? "bg-emerald-50" : "bg-hj-gray-light"}`}>
                      <i className={`${schema.icon} ${schema.active ? "text-emerald-600" : "text-hj-gray"}`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-hj-dark">{schema.type}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${schema.active ? "text-emerald-600 bg-emerald-50" : "text-hj-gray bg-hj-gray-light"}`}>
                          {schema.active ? "Aktiv" : "Inaktiv"}
                        </span>
                      </div>
                      <p className="text-xs text-hj-gray mt-0.5">{schema.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-lg border border-hj-gray-border p-5">
              <h3 className="text-sm font-bold text-hj-dark mb-3">{t("seo.schema.previewOrg")}</h3>
              <div className="bg-hj-gray-light rounded-md p-4 overflow-x-auto">
                <code className="text-xs text-hj-gray font-mono whitespace-pre leading-relaxed">
                  {JSON.stringify(orgSchema, null, 2)}
                </code>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-hj-gray-border p-5">
              <h3 className="text-sm font-bold text-hj-dark mb-3">{t("seo.schema.previewNews")}</h3>
              <div className="bg-hj-gray-light rounded-md p-4 overflow-x-auto">
                <code className="text-xs text-hj-gray font-mono whitespace-pre leading-relaxed">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "NewsArticle",
                    headline: "Bundesrat beschließt neue Klimaschutz-Massnahmen für 2026",
                    description: "Der Schweizer Bundesrat hat ein umfassendes Klimaschutz-Paket verabschiedet...",
                    image: "https://helvetik-journal.ch/images/artikel.jpg",
                    datePublished: "2026-04-27T08:30:00+01:00",
                    dateModified: "2026-04-27T14:00:00+01:00",
                    author: {
                      "@type": "Person",
                      name: "Markus Weber",
                      url: "https://helvetik-journal.ch/autor/markus-weber",
                    },
                    publisher: {
                      "@type": "Organization",
                      name: "Helvetik Journal",
                      logo: { "@type": "ImageObject", url: "https://helvetik-journal.ch/logo.png" },
                    },
                    mainEntityOfPage: "https://helvetik-journal.ch/artikel/bundesrat-klimaschutz-massnahmen-2026",
                    keywords: "Klima, Bundesrat, Umwelt, CO2",
                    articleSection: "Politik",
                    inLanguage: "de-CH",
                  }, null, 2)}
                </code>
              </div>
            </div>
          </div>
        )}

        {/* Robots Tab */}
        {activeTab === "robots" && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-hj-gray-border p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <label className="text-sm font-semibold text-hj-dark">{t("seo.robots.title")}</label>
                  <p className="text-xs text-hj-gray mt-0.5">
                    Steuert, welche Seiten von Suchmaschinen gecrawlt werden dürfen
                  </p>
                </div>
                <span className="text-[10px] text-hj-gray font-mono bg-hj-gray-light px-2 py-0.5 rounded">
                  /robots.txt
                </span>
              </div>
              <textarea
                value={settings.find((s) => s.key === "robots_txt")?.value || ""}
                onChange={(e) => updateSetting("robots_txt", e.target.value)}
                rows={12}
                className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <i className="ri-alert-line text-amber-600 mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-amber-800">{t("seo.robots.warningTitle")}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    {t("seo.robots.warningDesc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Checklist Tab */}
        {activeTab === "checklist" && (
          <div className="space-y-4">
            {categories.map((cat) => (
              <div key={cat} className="bg-white rounded-lg border border-hj-gray-border p-5">
                <h3 className="text-sm font-bold text-hj-dark mb-3">{cat}</h3>
                <div className="space-y-2">
                  {checklistItems
                    .filter((item) => item.category === cat)
                    .map((item) => (
                      <div key={item.label} className="flex items-center gap-3">
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 ${item.done ? "bg-emerald-100" : "bg-amber-100"}`}>
                          <i className={`text-xs ${item.done ? "ri-check-line text-emerald-600" : "ri-time-line text-amber-600"}`}></i>
                        </div>
                        <span className={`text-xs ${item.done ? "text-hj-dark" : "text-amber-700"}`}>
                          {item.label}
                        </span>
                        {!item.done && (
                          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full ml-auto">
                            {t("seo.checklist.pending")}
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
